import log from 'loglevel';

import { beginSnapshotSync, type SnapshotSyncSettlement } from './syncGate';
import { useComposeSnapshotIndexStore } from './useComposeSnapshotIndexStore';

const LOG_MESSAGES = {
  SAVE_FAILED: 'Background close-save failed',
} as const;

const backgroundSaves = new Map<string, Promise<void>>();

export const hasBackgroundSave = (sessionId: string): boolean =>
  backgroundSaves.has(sessionId);

// Registers synchronously, so the gate and the pending save exist before the compose unmounts.
export const trackBackgroundSave = (
  sessionId: string,
  work: () => Promise<SnapshotSyncSettlement>,
): Promise<void> => {
  const indexStore = useComposeSnapshotIndexStore.getState();
  // An unmount-triggered close registers after the compose already dropped its claim; take it back until settle.
  const isClaimedByCompose = Boolean(indexStore.liveSessionIds[sessionId]);
  indexStore.registerLiveSession(sessionId);
  indexStore.markBackgroundSaving(sessionId);
  const settleGate = beginSnapshotSync(sessionId);
  let saving: Promise<void> | undefined;
  saving = (async () => {
    let settlement: SnapshotSyncSettlement = {};
    try {
      settlement = await work();
    } catch (error) {
      log.error(LOG_MESSAGES.SAVE_FAILED, error);
    } finally {
      if (backgroundSaves.get(sessionId) === saving) {
        backgroundSaves.delete(sessionId);
      }
      if (settlement.draftId) {
        useComposeSnapshotIndexStore
          .getState()
          .recordSyncedDraft(sessionId, settlement.draftId);
      }
      if (!isClaimedByCompose) {
        useComposeSnapshotIndexStore
          .getState()
          .unregisterLiveSession(sessionId);
      }
      settleGate(settlement);
      useComposeSnapshotIndexStore.getState().unmarkBackgroundSaving(sessionId);
    }
  })();
  backgroundSaves.set(sessionId, saving);
  return saving;
};

// A save still in flight after unmount would be duplicated by the background sync if the session stopped counting as live.
export const runAfterBackgroundSave = (
  sessionId: string,
  action: () => void,
): void => {
  const saving = backgroundSaves.get(sessionId);
  if (!saving) {
    action();
    return;
  }
  void saving.then(action);
};

export const releaseLiveSession = (
  sessionId: string,
  savingSessionId: string = sessionId,
): void =>
  runAfterBackgroundSave(savingSessionId, () =>
    useComposeSnapshotIndexStore.getState().unregisterLiveSession(sessionId),
  );

export const releaseOpenCompose = (sessionId: string): void =>
  useComposeSnapshotIndexStore.getState().unregisterOpenCompose(sessionId);
